import { useTranslation } from 'react-i18next';
import type { MediaAsset } from '@ghostscreen/contracts';
import { MediaThumb } from '../content/MediaThumb';
import { contentKindLabel } from '../content/buildContentLibrary';

type PlaylistAddMediaTileProps = {
  asset: MediaAsset;
  busy: boolean;
  onAdd: (asset: MediaAsset) => void;
};

/**
 * Uniform add tile — 16:9 preview (Playlists.dc.html), filename, + Add.
 * Preview size never follows the source image/video shape.
 */
export function PlaylistAddMediaTile({ asset, busy, onAdd }: PlaylistAddMediaTileProps) {
  const { t } = useTranslation('playlists');
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => onAdd(asset)}
      className="flex w-full flex-col overflow-hidden rounded-xl border p-0 text-start transition hover:border-[var(--gs-accent)] disabled:opacity-50"
      style={{
        color: 'var(--gs-ink)',
        background: 'var(--gs-panel)',
        borderColor: 'var(--gs-line)',
      }}
    >
      <span
        className="relative block w-full shrink-0 overflow-hidden border-b"
        style={{
          aspectRatio: '16 / 9',
          background: 'var(--gs-panel2)',
          borderColor: 'var(--gs-line)',
        }}
      >
        {asset.previewUrl ? (
          <MediaThumb
            asset={asset}
            className="absolute inset-0 h-full w-full rounded-none border-0"
          />
        ) : (
          <span
            className="absolute inset-0 grid place-items-center font-mono text-[10px] uppercase tracking-[0.12em]"
            style={{ color: 'var(--gs-muted)' }}
          >
            {contentKindLabel(asset.kind)}
          </span>
        )}
      </span>
      <span className="flex flex-col gap-1 px-[11px] pb-[11px] pt-[9px]">
        <span className="line-clamp-2 min-h-[2.125rem] text-[13px] font-bold leading-tight">
          {asset.originalFilename}
        </span>
        <span className="text-xs font-bold" style={{ color: 'var(--gs-accent)' }}>
          {t('addMedia.add')}
        </span>
      </span>
    </button>
  );
}
